import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  ClockIcon,
  GlobeAltIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Badge } from '../../components/common/Badge';
import { Pagination } from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import { Checkbox } from '../../components/common/Checkbox';
import { Textarea } from '../../components/common/Textarea';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import { pimService } from '../../services/pimService';
import type { 
  ApiKey, 
  ApiKeyFormData, 
  ApiKeyFilters,
  ApiKeyScopeDefinition,
  ApiKeyUsageStatistics
} from '../../types/pim';

const SCOPE_DEFINITIONS: ApiKeyScopeDefinition[] = [
  {
    scope: 'pim.read',
    name: 'PIM Read',
    description: 'Read access to all PIM data',
    category: 'read',
    children: ['products.read', 'attributes.read']
  },
  {
    scope: 'pim.write',
    name: 'PIM Write',
    description: 'Write access to all PIM data',
    category: 'write',
    children: ['products.write', 'attributes.write']
  },
  {
    scope: 'pim.admin',
    name: 'PIM Admin',
    description: 'Full administrative access to PIM',
    category: 'admin',
    children: ['pim.read', 'pim.write', 'products.admin', 'attributes.admin']
  },
  {
    scope: 'products.read',
    name: 'Products Read',
    description: 'Read access to products',
    category: 'read',
    parent: 'pim.read'
  },
  {
    scope: 'products.write',
    name: 'Products Write',
    description: 'Create and update products',
    category: 'write',
    parent: 'pim.write'
  },
  {
    scope: 'products.admin',
    name: 'Products Admin',
    description: 'Full access to products including deletion',
    category: 'admin',
    parent: 'pim.admin'
  },
  {
    scope: 'attributes.read',
    name: 'Attributes Read',
    description: 'Read access to attributes',
    category: 'read',
    parent: 'pim.read'
  },
  {
    scope: 'attributes.write',
    name: 'Attributes Write',
    description: 'Create and update attributes',
    category: 'write',
    parent: 'pim.write'
  },
  {
    scope: 'attributes.admin',
    name: 'Attributes Admin',
    description: 'Full access to attributes including deletion',
    category: 'admin',
    parent: 'pim.admin'
  }
];

export const ApiKeysManagement = () => {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  // State management
  const [filters, setFilters] = useState<ApiKeyFilters>({
    page: 1,
    limit: 20,
    sort_by: 'created_at',
    sort_direction: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [revealedKeys, setRevealedKeys] = useState<Set<number>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<ApiKey | null>(null);
  
  // Auto-hide timers for revealed keys
  const revealTimers = useRef<Map<number, NodeJS.Timeout>>(new Map());

  // Form state
  const [formData, setFormData] = useState<ApiKeyFormData>({
    name: '',
    description: '',
    scopes: ['pim.read'],
    expiresAt: ''
  });

  // Fetch API keys
  const { data: apiKeysData, isLoading: loadingKeys, error: keysError } = useQuery({
    queryKey: ['pim-api-keys', filters],
    queryFn: () => pimService.getApiKeys(filters),
    keepPreviousData: true,
    retry: false,
    onError: (error: any) => {
      if (error.response?.status === 404) {
        showNotification('System PIM nie jest dostępny. Skontaktuj się z administratorem.', 'error');
      } else if (error.response?.status === 500) {
        showNotification('Błąd bazy danych. Tabele PIM mogą nie być zainicjowane.', 'error');
      } else {
        showNotification('Błąd podczas ładowania kluczy API. Spróbuj ponownie.', 'error');
      }
    }
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: ApiKeyFormData) => pimService.createApiKey(data),
    onSuccess: (response) => {
      showNotification('Klucz API został pomyślnie utworzony', 'success');
      queryClient.invalidateQueries(['pim-api-keys']);
      setShowCreateModal(false);
      setNewlyCreatedKey(response.data);
      setShowNewKeyModal(true);
      resetForm();
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas tworzenia klucza API', 'error');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ApiKeyFormData> }) => 
      pimService.updateApiKey(id, data),
    onSuccess: () => {
      showNotification('Klucz API został pomyślnie zaktualizowany', 'success');
      queryClient.invalidateQueries(['pim-api-keys']);
      setShowEditModal(false);
      setSelectedKey(null);
      resetForm();
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas aktualizacji klucza API', 'error');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => pimService.deleteApiKey(id),
    onSuccess: () => {
      showNotification('Klucz API został pomyślnie usunięty', 'success');
      queryClient.invalidateQueries(['pim-api-keys']);
      setShowDeleteModal(false);
      setSelectedKey(null);
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas usuwania klucza API', 'error');
    }
  });

  // Regenerate mutation
  const regenerateMutation = useMutation({
    mutationFn: (id: number) => pimService.regenerateApiKey(id),
    onSuccess: (response) => {
      showNotification('Klucz API został pomyślnie zregenerowany', 'success');
      queryClient.invalidateQueries(['pim-api-keys']);
      setNewlyCreatedKey(response.data);
      setShowNewKeyModal(true);
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas regeneracji klucza API', 'error');
    }
  });

  // Usage statistics query
  const { data: usageData } = useQuery({
    queryKey: ['api-key-usage', selectedKey?.id],
    queryFn: () => selectedKey ? pimService.getApiKeyUsageStatistics(selectedKey.id) : null,
    enabled: !!selectedKey && showUsageModal
  });

  // Cleanup effect for auto-hide timers
  useEffect(() => {
    return () => {
      revealTimers.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      scopes: ['pim.read'],
      expiresAt: ''
    });
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value || undefined,
      page: 1
    }));
  };

  const handleFilterChange = (key: keyof ApiKeyFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handleRevealKey = (keyId: number, fullKey: string) => {
    setRevealedKeys(prev => new Set([...prev, keyId]));
    
    // Clear existing timer for this key
    const existingTimer = revealTimers.current.get(keyId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    
    // Set auto-hide timer for 30 seconds
    const timer = setTimeout(() => {
      setRevealedKeys(prev => {
        const newSet = new Set(prev);
        newSet.delete(keyId);
        return newSet;
      });
      revealTimers.current.delete(keyId);
    }, 30000); // 30 seconds
    
    revealTimers.current.set(keyId, timer);
  };

  const handleHideKey = (keyId: number) => {
    setRevealedKeys(prev => {
      const newSet = new Set(prev);
      newSet.delete(keyId);
      return newSet;
    });
    
    const timer = revealTimers.current.get(keyId);
    if (timer) {
      clearTimeout(timer);
      revealTimers.current.delete(keyId);
    }
  };

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(key);
      showNotification('Klucz API skopiowany do schowka', 'success');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      showNotification('Błąd podczas kopiowania klucza API', 'error');
    }
  };

  const handleCreateKey = () => {
    setShowCreateModal(true);
    resetForm();
  };

  const handleEditKey = (key: ApiKey) => {
    setSelectedKey(key);
    setFormData({
      name: key.name || '',
      description: key.description || '',
      scopes: key.scopes || [],
      expiresAt: key.expires_at ? new Date(key.expires_at).toISOString().split('T')[0] : ''
    });
    setShowEditModal(true);
  };

  const handleDeleteKey = (key: ApiKey) => {
    setSelectedKey(key);
    setShowDeleteModal(true);
  };

  const handleViewUsage = (key: ApiKey) => {
    setSelectedKey(key);
    setShowUsageModal(true);
  };

  const handleTestKey = (key: ApiKey) => {
    setSelectedKey(key);
    setShowTestModal(true);
  };

  const handleRegenerateKey = (key: ApiKey) => {
    setSelectedKey(key);
    setShowRegenerateModal(true);
  };

  const confirmRegenerateKey = () => {
    if (selectedKey) {
      showNotification('Regeneracja klucza API...', 'info');
      regenerateMutation.mutate(selectedKey.id);
      setShowRegenerateModal(false);
      setSelectedKey(null);
    }
  };

  const handleScopeChange = (scope: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      scopes: checked
        ? [...prev.scopes, scope]
        : prev.scopes.filter(s => s !== scope)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedKey) {
      updateMutation.mutate({ id: selectedKey.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getScopeBadgeVariant = (category: string) => {
    switch (category) {
      case 'admin': return 'danger';
      case 'write': return 'warning';
      case 'read': return 'success';
      default: return 'secondary';
    }
  };

  const getStatusBadge = (key: ApiKey) => {
    if (!key.is_active) {
      return <Badge variant="secondary">{t('pim.apiKeys.statuses.inactive')}</Badge>;
    }

    if (key.expires_at && new Date(key.expires_at) < new Date()) {
      return <Badge variant="danger">{t('pim.apiKeys.statuses.expired')}</Badge>;
    }

    return <Badge variant="success">{t('pim.apiKeys.statuses.active')}</Badge>;
  };

  const formatLastUsed = (lastUsed: string | undefined) => {
    if (!lastUsed) return t('pim.apiKeys.usage.never');

    const date = new Date(lastUsed);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('pim.apiKeys.usage.today');
    if (diffDays === 1) return t('pim.apiKeys.usage.yesterday');
    if (diffDays < 7) return t('pim.apiKeys.usage.daysAgo', { count: diffDays });

    return date.toLocaleDateString();
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sort_by: 'created_at',
      sort_direction: 'desc'
    });
  };

  // Permission check
  if (!permissions.canManageApiKeys) {
    return (
      <div className="text-center py-12">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.common.accessDenied')}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('pim.common.accessDeniedDescription')}
        </p>
      </div>
    );
  }

  if (keysError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading API keys: {(keysError as any).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('pim.apiKeys.title')}</h1>
          <p className="text-gray-600">{t('pim.apiKeys.subtitle')}</p>
        </div>
        <Button icon={PlusIcon} onClick={handleCreateKey}>
          {t('pim.apiKeys.createKey')}
        </Button>
      </div>

      {/* Security Notice */}
      <Card>
        <CardContent>
          <div className="flex items-start space-x-3">
            <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-medium text-gray-900 mb-1">{t('pim.apiKeys.security.title')}</p>
              <ul className="space-y-1 text-sm">
                <li>• {t('pim.apiKeys.security.storeSecurely')}</li>
                <li>• {t('pim.apiKeys.security.useMinimumScopes')}</li>
                <li>• {t('pim.apiKeys.security.setExpiration')}</li>
                <li>• {t('pim.apiKeys.security.monitorUsage')}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder={t('pim.apiKeys.searchPlaceholder')}
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                icon={MagnifyingGlassIcon}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                icon={FunnelIcon}
                onClick={() => setShowFilters(!showFilters)}
              >
                {t('pim.apiKeys.filters')}
              </Button>
              {(filters.search || filters.is_active !== undefined) && (
                <Button
                  variant="outline"
                  icon={XMarkIcon}
                  onClick={clearFilters}
                >
                  {t('pim.apiKeys.clearFilters')}
                </Button>
              )}
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <Select
                label={t('pim.apiKeys.status')}
                value={filters.is_active?.toString() || ''}
                onChange={(e) => handleFilterChange('is_active', e.target.value ? e.target.value === 'true' : undefined)}
                options={[
                  { value: '', label: t('pim.apiKeys.allKeys') },
                  { value: 'true', label: t('pim.apiKeys.activeOnly') },
                  { value: 'false', label: t('pim.apiKeys.inactiveOnly') }
                ]}
              />
              <Select
                label={t('pim.apiKeys.sortBy')}
                value={filters.sort_by || 'created_at'}
                onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                options={[
                  { value: 'created_at', label: t('pim.apiKeys.createdDate') },
                  { value: 'last_used_at', label: t('pim.apiKeys.lastUsed') },
                  { value: 'name', label: t('pim.apiKeys.name') },
                  { value: 'usage_count', label: t('pim.apiKeys.usageCount') }
                ]}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* API Keys Table */}
      <Card>
        <CardContent>
          {loadingKeys ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-600">Loading API keys...</p>
            </div>
          ) : !apiKeysData?.data?.length ? (
            <div className="text-center py-12">
              <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No API keys</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating your first API key.</p>
              <div className="mt-6">
                <Button icon={PlusIcon} onClick={handleCreateKey}>
                  Create API Key
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name & Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      API Key
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scopes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Used
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {apiKeysData.data.map((key) => (
                    <tr key={key.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{key.name}</div>
                          {key.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {key.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                            {revealedKeys.has(key.id) ? key.key || key.maskedKey : key.maskedKey}
                          </code>
                          <div className="flex space-x-1">
                            {!revealedKeys.has(key.id) ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={EyeIcon}
                                onClick={() => handleRevealKey(key.id, key.key || key.maskedKey || '')}
                                title="Reveal key (30s timeout)"
                              />
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={EyeSlashIcon}
                                onClick={() => handleHideKey(key.id)}
                                title="Hide key"
                              />
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={copiedKey === (key.key || key.maskedKey) ? CheckIcon : ClipboardDocumentIcon}
                              onClick={() => handleCopyKey(revealedKeys.has(key.id) ? (key.key || key.maskedKey || '') : key.maskedKey || '')}
                              title="Copy to clipboard"
                              className={copiedKey === (key.key || key.maskedKey) ? 'text-green-600' : ''}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {key.scopes.slice(0, 2).map(scope => {
                            const scopeDef = SCOPE_DEFINITIONS.find(s => s.scope === scope);
                            return (
                              <Badge
                                key={scope}
                                variant={getScopeBadgeVariant(scopeDef?.category || 'read')}
                                size="sm"
                              >
                                {scopeDef?.name || scope}
                              </Badge>
                            );
                          })}
                          {key.scopes.length > 2 && (
                            <Badge variant="secondary" size="sm">
                              +{key.scopes.length - 2} more
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(key)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {key.usage_count} requests
                        </div>
                        {key.rate_limit_per_min && (
                          <div className="text-xs text-gray-500">
                            {key.rate_limit_per_min}/min limit
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {formatLastUsed(key.last_used_at)}
                        </div>
                        {key.last_used_ip && (
                          <div className="text-xs text-gray-500">
                            {key.last_used_ip}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={ChartBarIcon}
                            onClick={() => handleViewUsage(key)}
                            title="View usage statistics"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={PlayIcon}
                            onClick={() => handleTestKey(key)}
                            title="Test API key"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={PencilIcon}
                            onClick={() => handleEditKey(key)}
                            title="Edit key"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={ArrowPathIcon}
                            onClick={() => handleRegenerateKey(key)}
                            title="Regenerate key"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={TrashIcon}
                            onClick={() => handleDeleteKey(key)}
                            title="Delete key"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {apiKeysData?.pagination && (
            <div className="mt-6">
              <Pagination
                currentPage={apiKeysData.pagination.page}
                totalPages={apiKeysData.pagination.totalPages}
                totalItems={apiKeysData.pagination.total}
                itemsPerPage={apiKeysData.pagination.limit}
                onPageChange={(page) => handleFilterChange('page', page)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedKey(null);
          resetForm();
        }}
        title={selectedKey ? 'Edit API Key' : 'Create API Key'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Name *"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., E-commerce Integration"
              required
            />
          </div>

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Describe the purpose of this API key..."
            rows={3}
          />

          <Input
            label="Expiration Date"
            type="date"
            value={formData.expiresAt}
            onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
            min={new Date().toISOString().split('T')[0]}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Scopes & Permissions *
            </label>
            <div className="space-y-4">
              {['read', 'write', 'admin'].map(category => (
                <div key={category} className="border rounded-lg p-4">
                  <h4 className={`font-medium text-sm mb-2 ${
                    category === 'admin' ? 'text-red-700' : 
                    category === 'write' ? 'text-yellow-700' : 'text-green-700'
                  }`}>
                    {category.charAt(0).toUpperCase() + category.slice(1)} Permissions
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {SCOPE_DEFINITIONS
                      .filter(scope => scope.category === category)
                      .map(scope => (
                        <label key={scope.scope} className="flex items-start space-x-2">
                          <Checkbox
                            checked={formData.scopes.includes(scope.scope)}
                            onChange={(e) => handleScopeChange(scope.scope, e.target.checked)}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {scope.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {scope.description}
                            </div>
                          </div>
                        </label>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setSelectedKey(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading 
                ? 'Saving...' 
                : selectedKey ? 'Update Key' : 'Create Key'
              }
            </Button>
          </div>
        </form>
      </Modal>

      {/* New Key Display Modal */}
      <Modal
        isOpen={showNewKeyModal}
        onClose={() => {
          setShowNewKeyModal(false);
          setNewlyCreatedKey(null);
        }}
        title="API Key Created Successfully"
        size="lg"
      >
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Save This API Key Securely
                </h3>
                <p className="mt-1 text-sm text-yellow-700">
                  This is the only time the full API key will be displayed. 
                  Make sure to copy it and store it securely.
                </p>
              </div>
            </div>
          </div>

          {newlyCreatedKey && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                </label>
                <div className="flex items-center space-x-2">
                  <Input
                    value={newlyCreatedKey.key || ''}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    icon={copiedKey === newlyCreatedKey.key ? CheckIcon : ClipboardDocumentIcon}
                    onClick={() => handleCopyKey(newlyCreatedKey.key || '')}
                    className={copiedKey === newlyCreatedKey.key ? 'text-green-600' : ''}
                  >
                    {copiedKey === newlyCreatedKey.key ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Name:</span>
                  <span className="ml-2">{newlyCreatedKey.name}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Created:</span>
                  <span className="ml-2">{new Date(newlyCreatedKey.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={() => {
                setShowNewKeyModal(false);
                setNewlyCreatedKey(null);
              }}
            >
              I've Saved the Key
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete API Key"
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Permanent Action
                </h3>
                <p className="mt-1 text-sm text-red-700">
                  This action cannot be undone. Any applications using this key will lose access immediately.
                </p>
              </div>
            </div>
          </div>

          <p>
            Are you sure you want to delete the API key "{selectedKey?.name}"?
          </p>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => selectedKey && deleteMutation.mutate(selectedKey.id)}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? 'Deleting...' : 'Delete Key'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Usage Statistics Modal */}
      <Modal
        isOpen={showUsageModal}
        onClose={() => setShowUsageModal(false)}
        title={`Usage Statistics - ${selectedKey?.name}`}
        size="lg"
      >
        <div className="space-y-6">
          {usageData?.data ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-900">
                  {usageData.data.totalRequests || 0}
                </div>
                <div className="text-sm text-blue-700">Total Requests</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-900">
                  {usageData.data.requestsToday || 0}
                </div>
                <div className="text-sm text-green-700">Today</div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-900">
                  {usageData.data.requestsThisHour || 0}
                </div>
                <div className="text-sm text-yellow-700">This Hour</div>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-red-900">
                  {usageData.data.errorCount || 0}
                </div>
                <div className="text-sm text-red-700">Errors</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-gray-500">No usage data available</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Test API Key Modal */}
      <Modal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        title={`Test API Key - ${selectedKey?.name}`}
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Test your API key by making a sample request to the PIM API.
          </p>
          
          <Select
            label="Test Endpoint"
            options={[
              { value: '/api/v1/pim/products', label: 'List Products' },
              { value: '/api/v1/pim/attributes', label: 'List Attributes' },
              { value: '/api/v1/pim/product-types', label: 'List Product Types' }
            ]}
          />

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowTestModal(false)}>
              Cancel
            </Button>
            <Button icon={PlayIcon}>
              Run Test
            </Button>
          </div>
        </div>
      </Modal>

      {/* Regenerate API Key Confirmation Modal */}
      <Modal
        isOpen={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        title={t('pim.apiKeys.regenerate.title')}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-900 font-medium">
                {t('pim.apiKeys.regenerate.warning', { name: selectedKey?.name })}
              </p>
              <p className="text-gray-600 text-sm mt-1">
                {t('pim.apiKeys.regenerate.warningDesc')}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowRegenerateModal(false)}
            >
              {t('pim.apiKeys.regenerate.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={confirmRegenerateKey}
              icon={ArrowPathIcon}
            >
              {t('pim.apiKeys.regenerate.regenerate')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
